const PawblefyTheme = {
  text(key, fallback, values = {}) {
    const source = window.Pawblefy?.strings?.[key] || this.strings?.[key] || fallback;
    return String(source).replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/\[\[([a-z_]+)\]\]/g, (_, name) => String(values[name] ?? ''));
  },
  resolveVariant(variants, picks) {
    return variants.find((variant) => variant.options.every((value, index) => picks[String(index + 1)] === value)) || null;
  },
  matchingLeash(variants, color) {
    if (!color) return null;
    const normalized = color.trim().toLowerCase();
    return variants.find((variant) => variant.available && String(variant.title).trim().toLowerCase() === normalized) || null;
  },
  formatMoney(cents) {
    const value = (Number(cents || 0) / 100).toFixed(2);
    return (window.Pawblefy?.moneyFormat || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, value).replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(value))));
  },
  escapeHTML(value) {
    return String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[character]));
  },
  dateKey(date) {
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
  },
  calendarDate(year, month, day) {
    return new Date(Date.UTC(year, month, day, 12));
  },
  observedDate(date) {
    const observed = new Date(date);
    if (observed.getUTCDay() === 6) observed.setUTCDate(observed.getUTCDate() - 1);
    if (observed.getUTCDay() === 0) observed.setUTCDate(observed.getUTCDate() + 1);
    return observed;
  },
  nthWeekday(year, month, weekday, occurrence) {
    const date = this.calendarDate(year, month, 1);
    date.setUTCDate(1 + ((7 + weekday - date.getUTCDay()) % 7) + (occurrence - 1) * 7);
    return date;
  },
  lastWeekday(year, month, weekday) {
    const date = this.calendarDate(year, month + 1, 0);
    date.setUTCDate(date.getUTCDate() - ((7 + date.getUTCDay() - weekday) % 7));
    return date;
  },
  holidayKeys(year) {
    const fixed = [[0, 1], [5, 19], [6, 4], [10, 11], [11, 25]].map(([month, day]) => this.observedDate(this.calendarDate(year, month, day)));
    const floating = [
      this.nthWeekday(year, 0, 1, 3),
      this.nthWeekday(year, 1, 1, 3),
      this.lastWeekday(year, 4, 1),
      this.nthWeekday(year, 8, 1, 1),
      this.nthWeekday(year, 9, 1, 2),
      this.nthWeekday(year, 10, 4, 4)
    ];
    return [...fixed, ...floating].map((date) => this.dateKey(date));
  },
  isBusinessDay(date) {
    const weekday = date.getUTCDay();
    if (weekday === 0 || weekday === 6) return false;
    const years = [date.getUTCFullYear() - 1, date.getUTCFullYear(), date.getUTCFullYear() + 1];
    const holidays = new Set(years.flatMap((year) => this.holidayKeys(year)));
    return !holidays.has(this.dateKey(date));
  },
  addBusinessDays(start, amount) {
    const date = new Date(start);
    let added = 0;
    while (added < amount) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (this.isBusinessDay(date)) added += 1;
    }
    return date;
  },
  deliveryDetails() {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const today = this.calendarDate(Number(values.year), Number(values.month) - 1, Number(values.day));
    const ship = this.addBusinessDays(today, 1);
    const arrivalStart = this.addBusinessDays(ship, 2);
    const arrivalEnd = this.addBusinessDays(ship, 4);
    const formatter = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
    return {
      ship: formatter.format(ship),
      arrivalStart: formatter.format(arrivalStart),
      arrivalEnd: formatter.format(arrivalEnd),
      arrival: `${formatter.format(arrivalStart)} – ${formatter.format(arrivalEnd)}`
    };
  },
  shippingCutoffDetails() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23'
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const elapsed = (Number(values.hour) * 3600) + (Number(values.minute) * 60) + Number(values.second);
    const remaining = Math.max(60, 86400 - elapsed);
    const hours = Math.floor(remaining / 3600);
    const minutes = Math.max(1, Math.floor((remaining % 3600) / 60));
    return { time: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`, date: this.deliveryDetails().ship };
  },
  updateShippingCutoffs() {
    const cutoff = this.shippingCutoffDetails();
    document.querySelectorAll('[data-shipping-cutoff]').forEach((node) => {
      const template = node.dataset.cutoffTemplate || 'Order in [[time]] and it ships [[date]]';
      node.textContent = template.replace('[[time]]', cutoff.time).replace('[[date]]', cutoff.date);
    });
  },
  initDeliveryEstimates() {
    const details = this.deliveryDetails();
    document.querySelectorAll('[data-ships-date]').forEach((node) => { node.textContent = details.ship; });
    document.querySelectorAll('[data-arrival-start]').forEach((node) => { node.textContent = details.arrivalStart; });
    document.querySelectorAll('[data-arrival-end]').forEach((node) => { node.textContent = details.arrivalEnd; });
    document.querySelectorAll('[data-shipping-property="ship"]').forEach((input) => { input.value = `${details.ship} from the USA`; });
    document.querySelectorAll('[data-shipping-property="arrival"]').forEach((input) => { input.value = details.arrival; });
    this.delivery = details;
    this.updateShippingCutoffs();
    if (!this.shippingCutoffTimer) this.shippingCutoffTimer = window.setInterval(() => this.updateShippingCutoffs(), 60000);
  },
  async getCart() {
    const response = await fetch(`${window.Pawblefy?.routes?.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(this.text('cart_unavailable', 'Your bag could not be refreshed. Please open the bag page.'));
    return response.json();
  },
  nylonCount(cart) {
    return (cart?.items || []).filter((item) => String(item.product_id) === String(this.offerConfig?.productId)).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  },
  nylonUnitPrice(count) { return count >= 3 ? 1499 : count >= 2 ? 1999 : 2999; },
  nylonSavings(count) { return Math.max(0, count) * (2999 - this.nylonUnitPrice(count)); },
  freeShippingState(cart) {
    const config = this.offerConfig;
    const shipping = config?.shipping;
    const countries = Array.isArray(shipping?.countries) ? shipping.countries : [];
    const universal = !countries.length || countries.includes('ALL') || countries.includes('*');
    if (!shipping?.enabled || (cart?.currency || config.currency) !== 'USD' || (!universal && !countries.includes(shipping.country))) return null;
    const threshold = Number(shipping.threshold);
    if (!(threshold > 0)) return null;
    const progress = Math.max(0, Math.min(threshold, Number(cart?.total_price || 0)));
    return { threshold, progress, remaining: threshold - progress, unlocked: progress >= threshold };
  },
  matchingCartLeashes(cart) {
    const config = this.offerConfig;
    if (!config) return [];
    const needed = new Map();
    (cart?.items || []).forEach((item) => {
      if (String(item.product_id) !== String(config.productId)) return;
      const color = item.options_with_values?.find((option) => String(option.name).toLowerCase() === 'color')?.value || item.properties?.Color;
      const match = this.matchingLeash(config.leashVariants || [], color);
      if (match?.image) needed.set(String(match.id), { ...match, needed: (needed.get(String(match.id))?.needed || 0) + Number(item.quantity || 0) });
    });
    (cart?.items || []).forEach((item) => {
      if (String(item.product_id) !== String(config.leashProductId)) return;
      const match = needed.get(String(item.variant_id || item.id));
      if (match) match.needed -= Number(item.quantity || 0);
    });
    return [...needed.values()].filter((variant) => variant.needed > 0);
  },
  renderCartExtras(cart) {
    const state = this.freeShippingState(cart);
    document.querySelectorAll('[data-cart-shipping]').forEach((shipping) => {
      shipping.hidden = !state;
      if (state) {
        shipping.querySelector('[data-shipping-message]').textContent = state.unlocked ? this.text('shipping_unlocked', 'Free shipping unlocked!') : this.text('shipping_more', 'You’re [[amount]] away from free shipping.', { amount: this.formatMoney(state.remaining) });
        const progress = shipping.querySelector('[data-shipping-progress]');
        progress.max = state.threshold;
        progress.value = state.progress;
        progress.setAttribute('aria-valuetext', shipping.querySelector('[data-shipping-message]').textContent);
      }
    });
    document.querySelectorAll('[data-cart-savings]').forEach((savings) => {
      savings.hidden = !(cart.total_discount > 0);
      savings.querySelector('[data-cart-savings-amount]').textContent = `−${this.formatMoney(cart.total_discount)}`;
    });
    const options = this.matchingCartLeashes(cart);
    this.cartLeashOptions = options;
    document.querySelectorAll('[data-cart-leash]').forEach((box) => {
      const select = box.querySelector('[data-cart-leash-color]');
      const previous = select.value;
      select.innerHTML = options.map((variant) => `<option value="${this.escapeHTML(variant.id)}">${this.escapeHTML(variant.title)}</option>`).join('');
      if (options.some((variant) => String(variant.id) === previous)) select.value = previous;
      box.hidden = !options.length;
      this.updateCartLeashPreview(box);
    });
    document.querySelectorAll('[data-product-root]').forEach((root) => root.pawblefyProduct?.updateTotals());
  },
  updateCartLeashPreview(targetBox) {
    const boxes = targetBox ? [targetBox] : Array.from(document.querySelectorAll('[data-cart-leash]'));
    boxes.forEach((box) => {
      const select = box.querySelector('[data-cart-leash-color]');
      const image = box.querySelector('[data-cart-leash-image]');
      const button = box.querySelector('[data-cart-leash-add]');
      const variant = (this.cartLeashOptions || []).find((item) => String(item.id) === select?.value);
      if (variant) {
        image.src = variant.image;
        image.alt = this.text('leash_image_alt', '[[color]] matching nylon leash, shown with a collar. Leash sold separately.', { color: variant.title });
        image.hidden = false;
        button.textContent = this.text('leash_add', 'Add leash · [[price]]', { price: this.formatMoney(variant.price) });
      } else if (image) { image.hidden = true; image.removeAttribute('src'); }
      if (button) button.disabled = !variant || !!this.cartWriting;
    });
  },
  async writeCart(action) {
    if (this.cartWriting) return;
    this.cartWriting = true;
    const drawer = document.querySelector('[data-cart-drawer]');
    const surface = action.source?.closest('.cart-drawer__footer, .cart-page__footer') || document;
    const error = surface.querySelector('[data-cart-error]');
    const status = surface.querySelector('[data-cart-status]');
    if (error) { error.hidden = true; error.textContent = ''; }
    if (status) status.textContent = '';
    drawer?.setAttribute('aria-busy', 'true');
    document.querySelectorAll('[data-cart-leash-add], [data-cart-remove], [data-cart-leash-color]').forEach((node) => { node.disabled = true; });
    let confirmed = false;
    try {
      let response;
      try { response = await fetch(action.url, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(action.body) }); }
      catch { throw new Error(this.text('add_uncertain', 'We could not confirm the update. Review your bag before trying again.')); }
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || this.text('add_failed', 'Unable to update your bag. Please try again.'));
      }
      confirmed = true;
      await this.refreshCart(false);
      if (status) status.textContent = action.message || this.text('cart_updated', 'Bag updated. Subtotal: [[amount]].', { amount: this.formatMoney(this.cart.total_price) });
    } catch (problem) {
      if (error) {
        error.textContent = confirmed ? this.text('added_refresh_failed', 'Added successfully. Open your bag to review your items.') : problem.message;
        error.hidden = false;
        error.focus();
      }
    } finally {
      this.cartWriting = false;
      drawer?.removeAttribute('aria-busy');
      document.querySelectorAll('[data-cart-remove], [data-cart-leash-color]').forEach((node) => { node.disabled = false; });
      this.updateCartLeashPreview();
    }
  },
  initCartOffers() {
    const node = document.querySelector('[data-cart-offer-config]');
    try { this.offerConfig = node ? JSON.parse(node.textContent) : null; } catch { this.offerConfig = null; }
    if (this.offerConfig?.strings) this.strings = { ...this.strings, ...this.offerConfig.strings };
    document.querySelectorAll('[data-cart-leash]').forEach((box) => {
      box.querySelector('[data-cart-leash-color]')?.addEventListener('change', () => this.updateCartLeashPreview(box));
      box.querySelector('[data-cart-leash-add]')?.addEventListener('click', () => {
        const id = box.querySelector('[data-cart-leash-color]')?.value;
        const variant = this.matchingCartLeashes(this.cart).find((item) => String(item.id) === id);
        if (!variant) return;
        const delivery = this.deliveryDetails();
        this.writeCart({ source: box, url: window.Pawblefy?.routes?.add || '/cart/add.js', body: { items: [{ id: variant.id, quantity: 1, properties: { 'Matching color': variant.title, 'Ships by': `${delivery.ship} from the USA`, 'Estimated delivery': delivery.arrival } }] }, message: this.text('leash_added', '[[color]] matching leash added to your bag.', { color: variant.title }) });
      });
    });
    document.querySelector('[data-cart-items]')?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-remove]');
      if (!button || this.cartWriting) return;
      this.writeCart({ url: `${window.Pawblefy?.routes?.cart || '/cart'}/change.js`, body: { id: button.dataset.cartRemove, quantity: 0 } });
    });
  },
  async refreshCart(open = false) {
    const cart = await this.getCart();
    this.cart = cart;
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
    document.querySelectorAll('[data-cart-total]').forEach((total) => { total.textContent = this.formatMoney(cart.total_price); });
    const container = document.querySelector('[data-cart-items]');
    if (container) {
      container.innerHTML = cart.items.length ? cart.items.map((item) => `
        <div class="drawer-item">
          ${item.image ? `<img src="${this.escapeHTML(item.image)}" alt="">` : ''}
          <div><h3>${this.escapeHTML(item.product_title)}</h3><p>${this.escapeHTML(item.variant_title || '')}</p>${Object.entries(item.properties || {}).filter(([key, value]) => value && !key.startsWith('_')).map(([key, value]) => `<p>${this.escapeHTML(key)}: ${this.escapeHTML(value)}</p>`).join('')}<p class="drawer-item-prices">${item.quantity} × ${this.formatMoney(item.final_price)} ${item.original_price > item.final_price ? `<del>${this.formatMoney(item.original_price)}</del>` : ''}</p><button type="button" class="drawer-item-remove" data-cart-remove="${this.escapeHTML(item.key)}" aria-label="${this.escapeHTML(this.text('remove_item', 'Remove [[name]]', { name: item.product_title }))}">${this.escapeHTML(this.text('remove', 'Remove'))}</button></div>
        </div>`).join('') : `<p>${this.escapeHTML(this.text('empty_bag', 'Your bag is ready for something cute.'))}</p>`;
    }
    const count = this.nylonCount(cart);
    if (container && this.offerConfig?.enabled && (cart.currency || this.offerConfig.currency) === 'USD' && count > 0 && count < 3) {
      const personalizeUrl = document.body.classList.contains('template-index') ? '/pages/nc-v2#product' : `${this.offerConfig.productUrl}#personalize`;
      container.insertAdjacentHTML('beforeend', `<div class="bag-nudge"><p>${this.escapeHTML(this.text(count === 1 ? 'next_second' : 'next_third', count === 1 ? 'Add a second collar and both are $19.99 each, saving 33%.' : 'Add a third collar and every collar is $14.99 each, saving 50%.'))}</p><a href="${this.escapeHTML(personalizeUrl)}">${this.escapeHTML(this.text('next_collar_link', 'Personalize another collar'))}</a></div>`);
    }
    this.renderCartExtras(cart);
    if (open) this.openCart();
    return cart;
  },
  openCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || document.body.classList.contains('cart-open')) return;
    this.cartReturnFocus = document.activeElement;
    this.cartBackground = Array.from(document.body.children).filter((element) => !element.contains(drawer) && !element.matches('[data-cart-close], script, style, link'));
    this.cartBackground.forEach((element) => {
      element.dataset.cartWasInert = String(element.inert);
      element.inert = true;
    });
    document.body.classList.add('cart-open');
    drawer.inert = false;
    drawer.setAttribute('aria-hidden', 'false');
    (drawer.querySelector('[data-cart-close]') || drawer).focus();
  },
  closeCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    document.body.classList.remove('cart-open');
    if (drawer) {
      drawer.inert = true;
      drawer.setAttribute('aria-hidden', 'true');
    }
    (this.cartBackground || []).forEach((element) => {
      element.inert = element.dataset.cartWasInert === 'true';
      delete element.dataset.cartWasInert;
    });
    this.cartBackground = [];
    if (this.cartReturnFocus?.isConnected) this.cartReturnFocus.focus();
  },
  initHeader() {
    document.querySelector('[data-menu-toggle]')?.addEventListener('click', (event) => {
      const menu = document.querySelector('[data-main-nav]');
      const expanded = event.currentTarget.getAttribute('aria-expanded') === 'true';
      event.currentTarget.setAttribute('aria-expanded', String(!expanded));
      event.currentTarget.setAttribute('aria-label', expanded ? event.currentTarget.dataset.menuOpenLabel : event.currentTarget.dataset.menuCloseLabel);
      menu?.classList.toggle('open', !expanded);
    });
    document.querySelectorAll('[data-cart-open]').forEach((button) => button.addEventListener('click', (event) => { event.preventDefault(); this.openCart(); this.refreshCart(false).catch(() => {}); }));
    document.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', () => this.closeCart()));
    document.addEventListener('keydown', (event) => {
      if (!document.body.classList.contains('cart-open')) return;
      const drawer = document.querySelector('[data-cart-drawer]');
      if (event.key === 'Escape') { event.preventDefault(); this.closeCart(); return; }
      if (event.key !== 'Tab' || !drawer) return;
      const focusable = Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')).filter((node) => node.getClientRects().length && !node.closest('[hidden], [inert]'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); drawer.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    });
    const motionButton = document.querySelector('[data-motion-toggle]');
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const applyMotion = (paused) => {
      document.documentElement.dataset.motion = paused ? 'paused' : 'play';
      if (motionButton) {
        motionButton.setAttribute('aria-pressed', String(paused));
        motionButton.textContent = paused ? motionButton.dataset.motionPlayLabel : motionButton.dataset.motionPauseLabel;
      }
    };
    let motionOverride;
    try { motionOverride = sessionStorage.getItem('pawblefy-motion'); } catch {}
    applyMotion(motionOverride ? motionOverride === 'paused' : media.matches);
    motionButton?.addEventListener('click', () => {
      const paused = document.documentElement.dataset.motion !== 'paused';
      motionOverride = paused ? 'paused' : 'play';
      applyMotion(paused);
      try { sessionStorage.setItem('pawblefy-motion', motionOverride); } catch {}
    });
    media.addEventListener('change', (event) => { if (!motionOverride) applyMotion(event.matches); });
  },
  initGallery(root) {
    const track = root.querySelector('[data-gallery-track]');
    const thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
    const current = root.querySelector('[data-gallery-current]');
    const previous = root.querySelector('[data-gallery-previous]');
    const next = root.querySelector('[data-gallery-next]');
    if (!track || !thumbs.length) return;
    let activeIndex = 0;

    const selectImage = (index, scrollTrack = true, behavior = 'smooth') => {
      if (document.documentElement.dataset.motion === 'paused') behavior = 'auto';
      const safeIndex = Math.max(0, Math.min(index, thumbs.length - 1));
      activeIndex = safeIndex;
      thumbs.forEach((thumb, thumbIndex) => {
        const selected = thumbIndex === safeIndex;
        thumb.classList.toggle('active', selected);
        thumb.setAttribute('aria-current', String(selected));
      });
      if (current) current.textContent = String(safeIndex + 1);
      if (scrollTrack) track.scrollTo({ left: safeIndex * track.clientWidth, behavior });
      thumbs[safeIndex]?.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
    };

    thumbs.forEach((button, index) => button.addEventListener('click', () => selectImage(index)));
    previous?.addEventListener('click', () => selectImage((activeIndex - 1 + thumbs.length) % thumbs.length));
    next?.addEventListener('click', () => selectImage((activeIndex + 1) % thumbs.length));
    let galleryFrame;
    track.addEventListener('scroll', () => {
      window.cancelAnimationFrame(galleryFrame);
      galleryFrame = window.requestAnimationFrame(() => {
        if (!track.clientWidth) return;
        selectImage(Math.round(track.scrollLeft / track.clientWidth), false);
      });
    }, { passive: true });
  },
  initProduct(root) {
    const variantsNode = root.querySelector('[data-variants-json]');
    const variantSelect = root.querySelector('[name="id"]');
    const stack = root.querySelector('[data-set-stack]');
    const template = root.querySelector('[data-set-template]');
    if (!variantsNode || !variantSelect || !stack || !template) return;
    const stringsNode = root.querySelector('[data-commerce-strings]');
    if (stringsNode) this.strings = { ...this.strings, ...JSON.parse(stringsNode.textContent) };
    const personalized = root.dataset.personalized !== 'false';
    const form = root.querySelector('form.product-form');
    const offerConfig = this.offerConfig || {};
    if (offerConfig.strings) {
      this.strings = { ...this.strings, ...Object.fromEntries(Object.entries(offerConfig.strings).map(([key, value]) => [key.startsWith('offer_') ? key : `offer_${key}`, value])) };
    }
    const variants = JSON.parse(variantsNode.textContent);
    const offerEnabled = Boolean(offerConfig.enabled && root.dataset.nylonOffer === 'true' && String(offerConfig.productId) === root.dataset.productId && variants.length && variants.every((variant) => Number(variant.price) === 2999));
    const eligibleForOffer = (variant) => offerEnabled && Boolean(variant) && Number(variant.price) === 2999;
    const existingNylonCount = () => offerEnabled ? Math.max(0, Number(this.cart ? this.nylonCount?.(this.cart) || 0 : offerConfig.initialNylonCount || 0)) : 0;
    const unitPriceAt = (count) => offerEnabled ? this.nylonUnitPrice(count) : 2999;

    const leashVariantsNode = root.querySelector('[data-leash-variants-json]');
    const configuredLeashes = Array.isArray(offerConfig.leashVariants) ? offerConfig.leashVariants : [];
    const leashVariants = configuredLeashes.length ? configuredLeashes : leashVariantsNode ? JSON.parse(leashVariantsNode.textContent) : [];
    const cards = () => Array.from(stack.querySelectorAll('[data-set-card]'));
    const errorBox = root.querySelector('[data-product-error]');

    const showError = (message = '', focus = false) => {
      if (!errorBox) return;
      errorBox.hidden = !message;
      errorBox.textContent = message;
      if (message && focus) errorBox.focus();
    };

    const selectedButtons = (card) => Array.from(card.querySelectorAll('[data-set-option-button][aria-pressed="true"]'));
    const selectedVariant = (card) => {
      const picks = {};
      selectedButtons(card).forEach((button) => { picks[button.dataset.position] = button.dataset.value; });
      if (!card.querySelector('[data-set-option-button]')) return variants.find((variant) => String(variant.id) === variantSelect.value) || null;
      return this.resolveVariant(variants, picks);
    };

    const selectedColor = (card) => {
      const picker = Array.from(card.querySelectorAll('[data-set-variant-picker]')).find((node) => String(node.dataset.optionName).toLowerCase().includes('color'));
      const button = picker?.querySelector('[data-set-option-button][aria-pressed="true"]');
      return { name: button?.dataset.value || '', hex: button?.dataset.colorHex || '#22252b' };
    };

    const activateCard = (target, scroll = false) => {
      cards().forEach((card) => {
        const active = card === target;
        card.classList.toggle('active', active);
        card.classList.toggle('collapsed', !active);
        card.querySelector('[data-set-summary]')?.setAttribute('aria-expanded', String(active));
        const fields = card.querySelector('[data-set-fields]');
        if (fields) fields.hidden = !active;
        const editLabel = card.querySelector('[data-set-edit-label]');
        if (editLabel) editLabel.textContent = active ? this.text('editing', 'Editing') : this.text('edit', 'Edit');
        card.querySelectorAll('[data-set-name], [data-set-phone]').forEach((input) => { input.disabled = !active; });
      });
      if (scroll) {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
        target.querySelector('[data-set-name]')?.focus({ preventScroll: true });
      }
    };

    const syncLeash = (card, color) => {
      const addon = card.querySelector('[data-set-leash-addon]');
      const toggle = card.querySelector('[data-set-leash-toggle]');
      if (!addon || !toggle) return null;
      const leashVariant = this.matchingLeash(leashVariants, color.name);
      const label = card.querySelector('[data-set-leash-label]');
      const price = card.querySelector('[data-set-leash-price]');
      const image = card.querySelector('[data-set-leash-image]');
      const exactPhotoVariant = leashVariant && configuredLeashes.find((variant) => variant.available && String(variant.id) === String(leashVariant.id) && String(variant.title).trim().toLowerCase() === String(color.name).trim().toLowerCase());
      const imageSource = exactPhotoVariant?.image;
      const imageUrl = typeof imageSource === 'string' ? imageSource : imageSource?.url || imageSource?.src || '';
      if (image) {
        if (imageUrl) {
          if (image.getAttribute('src') !== imageUrl) image.src = imageUrl;
          image.alt = this.text('offer_leash_image_alt', 'Matching [[color]] leash', { color: leashVariant.title });
          image.hidden = false;
          image.onerror = () => { image.hidden = true; };
        } else {
          image.hidden = true;
          image.removeAttribute('src');
          image.removeAttribute('srcset');
          image.alt = '';
        }
      }
      if (!leashVariant) {
        toggle.checked = false;
        toggle.disabled = true;
        delete addon.dataset.variantId;
        delete addon.dataset.price;
        addon.classList.remove('active');
        if (label) label.textContent = this.text('leash_unavailable', 'A matching leash is unavailable for this color.');
        if (price) price.textContent = '';
        return null;
      }
      toggle.disabled = false;
      addon.dataset.variantId = leashVariant.id;
      addon.dataset.colorName = leashVariant.title;
      addon.dataset.price = leashVariant.price;
      addon.classList.toggle('active', toggle.checked);
      if (label) label.textContent = this.text('leash_match', 'We’ll add the [[color]] matching leash.', { color: leashVariant.title });
      if (price) price.textContent = `+${this.formatMoney(leashVariant.price)}`;
      return leashVariant;
    };

    const syncCard = (card) => {
      const variant = selectedVariant(card);
      const color = selectedColor(card);
      card.dataset.variantId = variant?.id || '';
      card.dataset.variantPrice = variant?.price || 0;
      card.dataset.variantAvailable = String(Boolean(variant?.available));
      card.dataset.colorName = color.name;

      card.querySelectorAll('[data-set-variant-picker]').forEach((picker) => {
        const selected = picker.querySelector('[data-set-option-button][aria-pressed="true"]');
        const label = picker.querySelector('[data-set-option-label]');
        if (selected && label) label.textContent = selected.dataset.value;
      });

      const preview = card.querySelector('[data-set-preview]');
      if (preview) preview.style.backgroundColor = color.hex;
      const colorPreviewLabel = card.querySelector('[data-set-color-preview-label]');
      if (colorPreviewLabel) colorPreviewLabel.textContent = this.text('color_preview', '[[color]] personalization preview', { color: color.name });

      const name = card.querySelector('[data-set-name]')?.value.trim();
      const number = Number(card.dataset.setIndex || 1);
      const nameSummary = card.querySelector('[data-set-name-summary]');
      const optionsSummary = card.querySelector('[data-set-options-summary]');
      if (nameSummary) nameSummary.textContent = name || this.text(personalized ? 'set_name' : 'item_name', personalized ? 'Personalized set [[number]]' : 'Your selection', { number });
      if (optionsSummary) optionsSummary.textContent = selectedButtons(card).map((button) => button.dataset.value).join(' · ') || variant?.title || '';
      syncLeash(card, color);
      return variant;
    };

    const updateIndexes = () => {
      const allCards = cards();
      const selectedNylonCount = allCards.filter((card) => eligibleForOffer(selectedVariant(card))).length;
      const nextOrdinal = existingNylonCount() + selectedNylonCount + 1;
      const nextPrice = unitPriceAt(nextOrdinal);
      allCards.forEach((card, index) => {
        const number = index + 1;
        card.dataset.setIndex = number;
        const numberLabel = card.querySelector('[data-set-number]');
        const heading = card.querySelector('[data-set-heading-number]');
        const remove = card.querySelector('[data-remove-set]');
        const addAnother = card.querySelector('[data-add-another-set]');
        if (numberLabel) numberLabel.textContent = number;
        if (heading) heading.textContent = this.text('set_heading', 'Set [[number]]', { number });
        if (remove) remove.hidden = number === 1;
        if (addAnother) {
          addAnother.hidden = number !== allCards.length || (!offerEnabled && allCards.length >= 3);
          addAnother.textContent = offerEnabled
            ? this.text(nextOrdinal === 2 ? 'offer_add_second' : nextOrdinal === 3 ? 'offer_add_third' : 'offer_add_another', 'Save this collar & add another: [[price]] each →', { price: this.formatMoney(nextPrice) })
            : this.text('add_another', 'Save set [[number]] & add another →', { number });
        }
      });
    };

    const updateTotals = () => {
      const allCards = cards();
      const cardVariants = allCards.map((card) => syncCard(card));
      const productSubtotal = cardVariants.reduce((sum, variant) => sum + Number(variant?.price || 0), 0);
      const existingCount = existingNylonCount();
      const incomingNylonCount = cardVariants.filter(eligibleForOffer).length;
      const tierUnitPrice = unitPriceAt(existingCount + incomingNylonCount);
      const incomingSavings = incomingNylonCount * (2999 - tierUnitPrice);
      const leashTotal = allCards.reduce((sum, card) => {
        const toggle = card.querySelector('[data-set-leash-toggle]');
        const addon = card.querySelector('[data-set-leash-addon]');
        return sum + (toggle?.checked ? Number(addon?.dataset.price || 0) : 0);
      }, 0);
      const total = productSubtotal + leashTotal - incomingSavings;
      const activeCard = allCards.find((card) => card.classList.contains('active')) || allCards[0];
      const activeVariant = selectedVariant(activeCard);
      const activeSavings = eligibleForOffer(activeVariant) ? Number(activeVariant.price) - tierUnitPrice : 0;
      const activePrice = Number(activeVariant?.price || 0) - activeSavings;

      variantSelect.value = activeVariant?.id || '';
      const price = root.querySelector('[data-product-price]');
      const compare = root.querySelector('[data-product-compare]');
      if (price) price.textContent = activeVariant ? this.formatMoney(activePrice) : this.text('unavailable', 'Unavailable');
      if (compare) {
        const hasCompare = activeVariant && (activeSavings > 0 || activeVariant.compare_at_price > activeVariant.price);
        compare.textContent = hasCompare ? this.formatMoney(activeSavings > 0 ? activeVariant.price : activeVariant.compare_at_price) : '';
        compare.hidden = !hasCompare;
      }
      const activePriceNote = root.querySelector('[data-nylon-active-price-note]');
      if (activePriceNote) {
        activePriceNote.hidden = activeSavings === 0;
        activePriceNote.textContent = activeSavings > 0 ? this.text('offer_active_price', 'Each collar with [[count]] eligible collars in your bag', { count: existingCount + incomingNylonCount }) : '';
      }

      root.querySelectorAll('[data-bundle-step]').forEach((step) => {
        const stepNumber = Number(step.dataset.bundleStep);
        const progressCount = offerEnabled ? Math.min(3, existingCount + incomingNylonCount) : allCards.length;
        step.classList.toggle('active', stepNumber === progressCount);
        step.classList.toggle('complete', stepNumber < progressCount);
      });
      const countLabel = root.querySelector('[data-bundle-count]');
      const leashLabel = root.querySelector('[data-bundle-leash-count]');
      const totalLabel = root.querySelector('[data-bundle-total]');
      const checkedLeashes = allCards.filter((card) => card.querySelector('[data-set-leash-toggle]')?.checked).length;
      if (countLabel) countLabel.textContent = this.text(!personalized ? 'item_count' : allCards.length === 1 ? 'set_count_one' : 'set_count_many', personalized ? '[[count]] personalized set(s)' : '[[count]] item', { count: allCards.length });
      if (leashLabel) {
        leashLabel.hidden = checkedLeashes === 0;
        leashLabel.textContent = checkedLeashes ? this.text('leash_count', 'Includes [[count]] matching leash(es)', { count: checkedLeashes }) : '';
      }
      if (totalLabel) totalLabel.textContent = cardVariants.every(Boolean) ? this.formatMoney(total) : 'Not available';
      const savingsLabel = root.querySelector('[data-bundle-savings]');
      const bundleCompare = root.querySelector('[data-bundle-compare]');
      const showSavings = offerEnabled && incomingSavings > 0 && cardVariants.every(Boolean);
      if (savingsLabel) {
        savingsLabel.hidden = !showSavings;
        savingsLabel.textContent = showSavings ? this.text('offer_incoming_savings', 'You save [[amount]] on these collars', { amount: this.formatMoney(incomingSavings) }) : '';
      }
      if (bundleCompare) {
        bundleCompare.hidden = !showSavings;
        bundleCompare.textContent = showSavings ? this.formatMoney(productSubtotal + leashTotal) : '';
      }
      const cartNote = root.querySelector('[data-nylon-cart-note]');
      if (cartNote) {
        cartNote.hidden = !offerEnabled || existingCount === 0;
        cartNote.textContent = offerEnabled && existingCount > 0
          ? this.text(existingCount === 1 ? 'offer_existing_one' : 'offer_existing_many', 'Your bag already has [[count]] nylon collar(s). Every eligible collar will be [[price]] each, including the ones already in your bag.', { count: existingCount, price: this.formatMoney(tierUnitPrice) })
          : '';
      }

      const submit = root.querySelector('[data-product-submit]');
      const allAvailable = cardVariants.every((variant) => variant?.available);
      if (submit) {
        submit.disabled = !allAvailable || form.dataset.submitting === 'true';
        if (form.dataset.submitting !== 'true') submit.textContent = allAvailable ? this.text('add_total', 'Add to bag · [[total]]', { total: this.formatMoney(total) }) : this.text('selection_unavailable', 'This selection is unavailable');
      }
      const url = new URL(window.location.href);
      if (activeVariant) url.searchParams.set('variant', activeVariant.id);
      else url.searchParams.delete('variant');
      window.history.replaceState({}, '', url);
      updateIndexes();
    };

    const validateCard = (card) => {
      if (!selectedVariant(card)?.available) {
        activateCard(card);
        showError(this.text('selection_unavailable', 'This selection is unavailable'), true);
        return false;
      }
      if (!personalized) return true;
      const name = card.querySelector('[data-set-name]');
      const phone = card.querySelector('[data-set-phone]');
      if (!name?.value.trim() || !phone?.value.trim()) {
        activateCard(card);
        showError(this.text('details_required', 'Add your pet’s name and phone number before continuing.'));
        const input = !name?.value.trim() ? name : phone;
        input?.focus();
        input?.reportValidity();
        return false;
      }
      showError();
      return true;
    };

    root.addEventListener('click', (event) => {
      const summary = event.target.closest('[data-set-summary]');
      if (summary) {
        activateCard(summary.closest('[data-set-card]'));
        updateTotals();
        return;
      }
      const option = event.target.closest('[data-set-option-button]');
      if (option) {
        const picker = option.closest('[data-set-variant-picker]');
        picker.querySelectorAll('[data-set-option-button]').forEach((button) => button.setAttribute('aria-pressed', String(button === option)));
        updateTotals();
        return;
      }
      const font = event.target.closest('[data-set-font-option]');
      if (font) {
        const card = font.closest('[data-set-card]');
        card.querySelectorAll('[data-set-font-option]').forEach((button) => button.setAttribute('aria-pressed', String(button === font)));
        card.querySelector('[data-set-font-input]').value = font.dataset.fontName;
        card.querySelector('[data-set-font-label]').textContent = font.dataset.fontName;
        card.querySelector('[data-set-preview]').style.fontFamily = `"${font.dataset.fontFamily}", sans-serif`;
        return;
      }
      const fontColor = event.target.closest('[data-set-font-color-option]');
      if (fontColor) {
        const card = fontColor.closest('[data-set-card]');
        card.querySelectorAll('[data-set-font-color-option]').forEach((button) => button.setAttribute('aria-pressed', String(button === fontColor)));
        card.querySelector('[data-set-font-color-input]').value = fontColor.dataset.colorName;
        card.querySelector('[data-set-font-color-label]').textContent = fontColor.dataset.colorName;
        card.querySelector('[data-set-preview]').style.color = fontColor.dataset.colorValue;
        return;
      }
      const addAnother = event.target.closest('[data-add-another-set]');
      if (addAnother) {
        const current = addAnother.closest('[data-set-card]');
        if (!validateCard(current) || (!offerEnabled && cards().length >= 3)) return;
        const fragment = template.content.cloneNode(true);
        const nextCard = fragment.querySelector('[data-set-card]');
        nextCard.querySelectorAll('[name^="properties["]').forEach((input) => input.removeAttribute('name'));
        nextCard.querySelectorAll('[data-js-only]').forEach((node) => { node.hidden = false; });
        nextCard.querySelectorAll('[data-native-only]').forEach((node) => { node.hidden = true; });
        stack.appendChild(fragment);
        updateIndexes();
        activateCard(nextCard, true);
        updateTotals();
        return;
      }
      const remove = event.target.closest('[data-remove-set]');
      if (remove) {
        const card = remove.closest('[data-set-card]');
        const allCards = cards();
        const index = allCards.indexOf(card);
        card.remove();
        const remaining = cards();
        updateIndexes();
        activateCard(remaining[Math.max(0, index - 1)] || remaining[0]);
        updateTotals();
      }
    });

    root.addEventListener('input', (event) => {
      const card = event.target.closest('[data-set-card]');
      if (!card) return;
      if (event.target.matches('[data-set-name]')) {
        card.querySelector('[data-set-name-preview]').textContent = event.target.value || this.text('preview_name', 'YOUR PUP');
      }
      if (event.target.matches('[data-set-phone]')) {
        card.querySelector('[data-set-phone-preview]').textContent = event.target.value || this.text('preview_phone', 'YOUR NUMBER');
      }
      showError();
      syncCard(card);
    });

    root.addEventListener('change', (event) => {
      if (!event.target.matches('[data-set-leash-toggle]')) return;
      event.target.closest('[data-set-leash-addon]')?.classList.toggle('active', event.target.checked);
      updateTotals();
    });

    updateIndexes();
    activateCard(cards()[0]);
    updateTotals();
    this.initGallery(root);
    root.pawblefyProduct = { updateTotals, validate: () => cards().every(validateCard), personalized };
    form.querySelectorAll('[data-set-card] [name^="properties["]').forEach((input) => input.removeAttribute('name'));
    form.noValidate = true;
    variantSelect.hidden = true;
    root.querySelectorAll('[data-js-only]').forEach((node) => { node.hidden = false; });
    root.querySelectorAll('[data-native-only]').forEach((node) => { node.hidden = true; });
  },
  initForms() {
    document.querySelectorAll('[data-product-root]').forEach((root) => this.initProduct(root));
    document.querySelectorAll('form.product-form').forEach((form) => form.addEventListener('submit', async (event) => {
      const root = form.closest('[data-product-root]');
      if (!root?.pawblefyProduct) return;
      event.preventDefault();
      if (form.dataset.submitting === 'true' || !root.pawblefyProduct.validate()) return;
      root.pawblefyProduct.updateTotals();
      const cards = Array.from(form.querySelectorAll('[data-set-card]'));
      const errorBox = form.querySelector('[data-product-error]');
      const statusBox = form.querySelector('[data-product-status]');
      const button = form.querySelector('[type="submit"]');
      form.dataset.submitting = 'true';
      form.setAttribute('aria-busy', 'true');
      form.inert = true;
      if (errorBox) errorBox.hidden = true;
      if (statusBox) statusBox.hidden = true;
      if (button) { button.disabled = true; button.textContent = this.text('adding', 'Adding to your bag…'); }
      try {
        const items = [];
        const delivery = this.deliveryDetails();
        cards.forEach((card, index) => {
          const group = window.crypto.randomUUID();
          const properties = root.pawblefyProduct.personalized ? {
            'Pet name': card.querySelector('[data-set-name]').value.trim(),
            'Phone number': card.querySelector('[data-set-phone]').value.trim(),
            'Font': card.querySelector('[data-set-font-input]').value,
            'Font color': card.querySelector('[data-set-font-color-input]').value,
            'Set number': `${index + 1} of ${cards.length}`,
            '_Personalization group': group
          } : {};
          properties['Ships by'] = `${delivery.ship} from the USA`;
          properties['Estimated delivery'] = delivery.arrival;
          card.querySelectorAll('[data-set-option-button][aria-pressed="true"]').forEach((option) => { properties[option.dataset.optionName] = option.dataset.value; });
          items.push({ id: Number(card.dataset.variantId), quantity: 1, properties });

          const leashAddon = card.querySelector('[data-set-leash-addon]');
          const leashToggle = card.querySelector('[data-set-leash-toggle]');
          if (leashAddon && leashToggle?.checked && leashAddon.dataset.variantId) {
            items.push({
              id: Number(leashAddon.dataset.variantId),
              quantity: 1,
              properties: {
                'Matching color': leashAddon.dataset.colorName,
                'Add-on for': leashAddon.dataset.sourceProduct,
                'Set number': `${index + 1} of ${cards.length}`,
                'Ships by': `${delivery.ship} from the USA`,
                'Estimated delivery': delivery.arrival,
                '_Personalization group': group
              }
            });
          }
        });

        let response;
        try {
          response = await fetch(window.Pawblefy.routes.add, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
          });
        } catch {
          throw new Error(this.text('add_uncertain', 'We could not confirm the update. Review your bag before trying again.'));
        }
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || this.text('add_failed', 'Unable to add this selection. Please check your options.'));
        }
        form.inert = false;
        let refreshed = false;
        try { await this.refreshCart(true); refreshed = true; } catch {}
        if (refreshed && document.body.classList.contains('cart-open')) this.cartReturnFocus = button;
        if (errorBox) errorBox.hidden = true;
        if (statusBox) {
          statusBox.hidden = false;
          statusBox.querySelector('[data-product-status-text]').textContent = this.text(refreshed ? 'added' : 'added_refresh_failed', refreshed ? 'Added to your bag.' : 'Added successfully. Open your bag to review your items.');
        }
      } catch (error) {
        form.inert = false;
        if (errorBox) { errorBox.hidden = false; errorBox.textContent = error.message; errorBox.focus(); }
        if (statusBox) {
          statusBox.hidden = false;
          statusBox.querySelector('[data-product-status-text]').textContent = '';
        }
      } finally {
        form.inert = false;
        form.dataset.submitting = 'false';
        form.removeAttribute('aria-busy');
        root.pawblefyProduct.updateTotals();
      }
    }));
  },
  init() { this.initDeliveryEstimates(); this.initCartOffers(); this.initHeader(); this.initForms(); this.refreshCart(false).catch(() => {}); }
};

document.addEventListener('DOMContentLoaded', () => PawblefyTheme.init());
